import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, TableCellProps, TextField } from "@material-ui/core";
import { Link } from "react-router-dom";
import { Typography } from "@welldigital/components";
import CustomTable, {
  CustomTableHeaderProps,
} from "pages/ClinicalReviewPage/components/Table";
import Pagination from "pages/ClinicalReviewPage/components/Pagination";
import {
  fetchSubscriptionsList,
  getSubscriptionDetails,
  updateSubscriptionNextOrderDate,
} from "pages/ClinicalReviewPage/ClinicalReviewApi";
import {
  ClinicalSubscription,
  ClinicalSubscriptionsResponse,
  SubscriptionDetailsType,
} from "pages/ClinicalReviewPage/Types";
import { useCurrentUserEmail } from "services/auth-hooks";
import { formatDate } from "utils/time";
import "./index.scss";

const PAGE_SIZE = 10;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const CELL_PROPS: TableCellProps = { align: "left" };

type SubscriptionTableRow = ClinicalSubscription & {
  nextOrderDate?: string;
  daysUntilNextOrder?: number;
};

type SubscriptionSortKey =
  | ""
  | "latestOrderId"
  | "customerEmail"
  | "subscriptionIssueNumber"
  | "latestOrderStatus"
  | "nextOrderDate"
  | "daysUntilNextOrder";

const calculateDaysUntil = (nextOrderDate: string | undefined) => {
  if (!nextOrderDate) {
    return undefined;
  }

  const targetDate = new Date(nextOrderDate);
  if (Number.isNaN(targetDate.getTime())) {
    return undefined;
  }

  const now = new Date();
  const startOfTodayUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  const startOfTargetUTC = Date.UTC(
    targetDate.getUTCFullYear(),
    targetDate.getUTCMonth(),
    targetDate.getUTCDate(),
  );

  return Math.ceil((startOfTargetUTC - startOfTodayUTC) / DAY_IN_MS);
};

const toDateInputValue = (dateValue: string | undefined) => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const year = parsedDate.getUTCFullYear();
  const month = `${parsedDate.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${parsedDate.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toIsoFromDateInput = (dateValue: string) => {
  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) {
    return "";
  }

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
};

const getDateSortValue = (dateValue?: string) => {
  if (!dateValue) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const compareText = (left?: string, right?: string) =>
  (left || "").localeCompare(right || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });

const compareNumber = (left?: number, right?: number) =>
  (left ?? Number.NEGATIVE_INFINITY) - (right ?? Number.NEGATIVE_INFINITY);

const formatSubscriptionStatus = (status?: string) => {
  const normalizedStatus = status?.trim().toLowerCase();

  if (!normalizedStatus) {
    return "-";
  }

  if (normalizedStatus === "cancel") {
    return "Cancelled";
  }

  return normalizedStatus
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
};

const compareSubscriptionRows = (
  left: SubscriptionTableRow,
  right: SubscriptionTableRow,
  sortKey: SubscriptionSortKey,
) => {
  switch (sortKey) {
    case "latestOrderId":
      return compareNumber(left.latestOrderId, right.latestOrderId);
    case "customerEmail":
      return compareText(left.customerEmail, right.customerEmail);
    case "subscriptionIssueNumber":
      return compareNumber(
        left.subscriptionIssueNumber,
        right.subscriptionIssueNumber,
      );
    case "latestOrderStatus":
      return compareText(left.latestOrderStatus, right.latestOrderStatus);
    case "nextOrderDate":
      return compareNumber(
        getDateSortValue(left.nextOrderDate),
        getDateSortValue(right.nextOrderDate),
      );
    case "daysUntilNextOrder":
      return compareNumber(left.daysUntilNextOrder, right.daysUntilNextOrder);
    default:
      return 0;
  }
};

const getRequestErrorMessage = (error: unknown) => {
  const requestError = error as {
    response?: { data?: { message?: string; error?: string } | string };
    message?: string;
  };

  const responseData = requestError?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }
  if (
    responseData &&
    typeof responseData === "object" &&
    "message" in responseData &&
    typeof responseData.message === "string" &&
    responseData.message.trim()
  ) {
    return responseData.message;
  }
  if (
    responseData &&
    typeof responseData === "object" &&
    "error" in responseData &&
    typeof responseData.error === "string" &&
    responseData.error.trim()
  ) {
    return responseData.error;
  }

  return requestError?.message || "Unable to update next order date";
};

const ClinicalReviewSubscriptionsPage: React.FC = () => {
  const email = useCurrentUserEmail();
  const [subscriptions, setSubscriptions] = useState<SubscriptionTableRow[]>(
    [],
  );
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<SubscriptionSortKey>("");
  const [isAscending, setIsAscending] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    string | null
  >(null);
  const [editingNextOrderDate, setEditingNextOrderDate] = useState("");
  const [savingSubscriptionId, setSavingSubscriptionId] = useState<
    string | null
  >(null);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setEditingSubscriptionId(null);
    setEditingNextOrderDate("");
    try {
      const response = (await fetchSubscriptionsList({
        page: currentPage,
        limit: PAGE_SIZE,
      })) as ClinicalSubscriptionsResponse;

      const rows = response?.subscriptions ?? [];
      const detailsResp = await Promise.allSettled(
        rows.map((row) => getSubscriptionDetails(row.subscriptionId)),
      );
      const enrichedRows = rows.map((row, index) => {
        const detailResp = detailsResp[index];
        if (detailResp.status !== "fulfilled" || !detailResp.value) {
          return row;
        }

        const details = detailResp.value as SubscriptionDetailsType;

        return {
          ...row,
          nextOrderDate: details.nextOrderDate,
          daysUntilNextOrder: calculateDaysUntil(details.nextOrderDate),
          subscriptionStatus: details.status ?? row.subscriptionStatus,
        };
      });

      setSubscriptions(enrichedRows);
      setTotalItems(response?.total ?? 0);
    } catch (error) {
      setSubscriptions([]);
      setTotalItems(0);
      setLoadError("Unable to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const startEditingNextOrderDate = useCallback((row: SubscriptionTableRow) => {
    setEditingSubscriptionId(row.subscriptionId);
    setEditingNextOrderDate(toDateInputValue(row.nextOrderDate));
    setUpdateError("");
    setUpdateSuccess("");
  }, []);

  const onSortChange = useCallback(
    (nextSortKeyName: string) => {
      const nextSortKey = nextSortKeyName as SubscriptionSortKey;
      if (sortKey === nextSortKey) {
        setIsAscending((currentValue) => !currentValue);
        return;
      }

      setSortKey(nextSortKey);
      setIsAscending(true);
    },
    [sortKey],
  );

  const stopEditingNextOrderDate = useCallback(() => {
    setEditingSubscriptionId(null);
    setEditingNextOrderDate("");
    setUpdateError("");
  }, []);

  const saveNextOrderDate = useCallback(
    async (subscriptionId: string) => {
      if (!editingNextOrderDate) {
        setUpdateError("Please select a date");
        return;
      }

      setSavingSubscriptionId(subscriptionId);
      setUpdateError("");
      setUpdateSuccess("");

      try {
        await updateSubscriptionNextOrderDate(subscriptionId, {
          nextOrderDate: editingNextOrderDate,
          updatedBy: email || "clinical-review",
        });

        let canonicalDate = toIsoFromDateInput(editingNextOrderDate);
        try {
          const details = await getSubscriptionDetails(subscriptionId);
          if (details?.nextOrderDate) {
            const persistedDate = toDateInputValue(details.nextOrderDate);
            if (persistedDate === editingNextOrderDate) {
              canonicalDate = details.nextOrderDate;
            }
          }

          setSubscriptions((prevState) =>
            prevState.map((row) => {
              if (row.subscriptionId !== subscriptionId) {
                return row;
              }

              const nextOrderDate = canonicalDate || row.nextOrderDate;
              return {
                ...row,
                nextOrderDate,
                daysUntilNextOrder: calculateDaysUntil(nextOrderDate),
                subscriptionStatus: details.status ?? row.subscriptionStatus,
              };
            }),
          );
        } catch (error) {
          // Fallback to the selected date value when details refresh fails.
          setSubscriptions((prevState) =>
            prevState.map((row) => {
              if (row.subscriptionId !== subscriptionId) {
                return row;
              }

              const nextOrderDate = canonicalDate || row.nextOrderDate;
              return {
                ...row,
                nextOrderDate,
                daysUntilNextOrder: calculateDaysUntil(nextOrderDate),
              };
            }),
          );
        }
        setUpdateSuccess(
          `Next order date updated for subscription ${subscriptionId}`,
        );
        setEditingSubscriptionId(null);
        setEditingNextOrderDate("");
      } catch (error) {
        setUpdateError(getRequestErrorMessage(error));
      } finally {
        setSavingSubscriptionId(null);
      }
    },
    [editingNextOrderDate, email],
  );

  const sortedSubscriptions = useMemo(() => {
    if (!sortKey) {
      return subscriptions;
    }

    return [...subscriptions].sort((left, right) => {
      const comparison = compareSubscriptionRows(left, right, sortKey);
      return isAscending ? comparison : -comparison;
    });
  }, [isAscending, sortKey, subscriptions]);

  const columns: CustomTableHeaderProps<SubscriptionTableRow>[] = useMemo(
    () => [
      {
        header: "Latest Order",
        keyExtractor: "latestOrderId",
        isSortable: true,
        sortKeyName: "latestOrderId",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "16%" },
        transform: (data) => (
          <Link to={`/clinical-review/order/${data.latestOrderId}`}>
            {data.latestOrderId}
          </Link>
        ),
      },
      {
        header: "Customer Email",
        keyExtractor: "customerEmail",
        isSortable: true,
        sortKeyName: "customerEmail",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "14%" },
        transform: (data) => data.customerEmail || "-",
      },
      {
        header: "Issue",
        keyExtractor: "subscriptionIssueNumber",
        isSortable: true,
        sortKeyName: "subscriptionIssueNumber",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "12%" },
        transform: (data) => {
          if (!data.subscriptionIssueNumber) {
            return "-";
          }
          return data.maxIssueLimit
            ? `${data.subscriptionIssueNumber} of ${data.maxIssueLimit}`
            : data.subscriptionIssueNumber;
        },
      },
      {
        header: "Order Status",
        keyExtractor: "latestOrderStatus",
        isSortable: true,
        sortKeyName: "latestOrderStatus",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "14%" },
      },
      {
        header: "Next Order Date",
        keyExtractor: "nextOrderDate",
        isSortable: true,
        sortKeyName: "nextOrderDate",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "17%" },
        transform: (data) => {
          const isEditing = editingSubscriptionId === data.subscriptionId;
          const isSaving = savingSubscriptionId === data.subscriptionId;

          if (isEditing) {
            return (
              <div className="crp-next-order-editor">
                <TextField
                  type="date"
                  value={editingNextOrderDate}
                  onChange={(event) =>
                    setEditingNextOrderDate(event.target.value)
                  }
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <div className="crp-next-order-editor-actions">
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={() => saveNextOrderDate(data.subscriptionId)}
                    disabled={isSaving || !editingNextOrderDate}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    onClick={stopEditingNextOrderDate}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div className="crp-next-order-display">
              <span>
                {data.nextOrderDate
                  ? formatDate(data.nextOrderDate, "dd/MM/yy")
                  : "-"}
              </span>
              <Button
                type="button"
                color="primary"
                size="small"
                onClick={() => startEditingNextOrderDate(data)}
              >
                Edit
              </Button>
            </div>
          );
        },
      },
      {
        header: "Days To Next",
        keyExtractor: "daysUntilNextOrder",
        isSortable: true,
        sortKeyName: "daysUntilNextOrder",
        onSortChange,
        cellProps: { ...CELL_PROPS, width: "10%" },
        transform: (data) => {
          if (data.daysUntilNextOrder === undefined) {
            return "-";
          }
          if (data.daysUntilNextOrder < 0) {
            return `Overdue ${Math.abs(data.daysUntilNextOrder)}d`;
          }
          if (data.daysUntilNextOrder === 0) {
            return "Today";
          }
          return `${data.daysUntilNextOrder}d`;
        },
      },
      {
        header: "Status",
        keyExtractor: "subscriptionStatus",
        cellProps: { ...CELL_PROPS, width: "12%" },
        transform: (data) => formatSubscriptionStatus(data.subscriptionStatus),
      },
    ],
    [
      editingSubscriptionId,
      editingNextOrderDate,
      onSortChange,
      saveNextOrderDate,
      savingSubscriptionId,
      startEditingNextOrderDate,
      stopEditingNextOrderDate,
    ],
  );

  return (
    <div className="crp-subscriptions-container">
      <Typography className="crp-subscriptions-title">Subscriptions</Typography>

      {loadError ? (
        <Typography className="crp-subscriptions-message">
          {loadError}
        </Typography>
      ) : null}
      {updateError ? (
        <Typography className="crp-subscriptions-message">
          {updateError}
        </Typography>
      ) : null}
      {updateSuccess ? (
        <Typography className="crp-subscriptions-message-success">
          {updateSuccess}
        </Typography>
      ) : null}

      <CustomTable
        sortKey={sortKey}
        isAscending={isAscending}
        data={sortedSubscriptions}
        loading={loading}
        columns={columns}
      />

      {!loading && sortedSubscriptions.length === 0 ? (
        <Typography>No subscriptions found</Typography>
      ) : null}

      <Pagination
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default ClinicalReviewSubscriptionsPage;
